import React from 'react';
import { Col, Divider, Row } from 'antd';
import { PrintDateShort } from '../../utils/timeParser';
import Wrapper from './style';
import { INTEGRATOR_FACEBOOK } from '../../configs/integrator';
import { toIDR } from '../../utils/commonUtils';

const { GENDER_OPTION } = INTEGRATOR_FACEBOOK();

const onGetDate = (date) => {
  if (!date) return false;
  return PrintDateShort(date);
};

const displayValue = (value) => {
  if (value) {
    const text = value.replace(/_/g, ' ');
    const parse = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    return parse;
  }
  return value;
};

const Description = (props) => {
  const { label, value, emptyText = '-', onParseValue } = props;
  let display = value;
  if (onParseValue) {
    display = displayValue(value);
  }

  return (
    <Row gutter={[4, 4]}>
      {label && (
        <Col
          span={24}
          className="font-paragraph_medium black3 description_label"
        >
          {label}
        </Col>
      )}
      <Col
        span={24}
        className={`font-paragraph ${
          value ? 'black1' : 'black4'
        } description_value`}
      >
        {display || emptyText}
      </Col>
    </Row>
  );
};

const AdSet = (props) => {
  const { dataSource } = props;

  const campaignData = dataSource?.data?.facebook_campaign || {};
  const adSetData = dataSource?.data?.facebook_adset || {};
  const startDate = onGetDate(adSetData?.start_time);
  const endDate = onGetDate(adSetData?.end_time);

  const onGetSpendLimit = () => {
    if (adSetData?.daily_spend_cap) {
      return {
        required: true,
        min: adSetData?.daily_min_spend_target,
        max: adSetData?.daily_spend_cap,
        label: 'Daily',
      };
    }
    if (adSetData?.lifetime_spend_cap) {
      return {
        required: true,
        min: adSetData?.lifetime_min_spend_target,
        max: adSetData?.lifetime_spend_cap,
        label: 'Lifetime',
      };
    }
    return {
      required: false,
      min: null,
      max: null,
      label: 'Daily',
    };
  };

  const onGetCampaignBudget = () => {
    const data = adSetData?.budget_type || campaignData?.budget_type;
    if (!data) return false;
    return `${displayValue(data?.name)} - ${toIDR(data?.amount)}`;
  };

  const spendLimitData = onGetSpendLimit();

  const onGetLanguages = () => {
    const values = adSetData?.targeting?.locales;
    if (!values) return '';
    return values
      .map((item) => {
        return item?.name;
      })
      .join(', ');
  };

  const onGetLocation = () => {
    const location = adSetData?.targeting?.location || [];
    if (location?.length === 0) {
      return false;
    }
    return location
      ?.map((item) => {
        return item?.name;
      })
      .join(', ');
  };

  const onGetGender = () => {
    const value = adSetData?.targeting?.genders?.[0];
    const selected = GENDER_OPTION?.find((item) => {
      return item?.value === value;
    });
    return selected?.label;
  };

  const onGetDetailTargeting = () => {
    const include = adSetData?.targeting?.flexible_spec || [];
    const exclude = adSetData?.targeting?.exclusions || [];
    const parse = (arr) => {
      if (arr?.length > 0) {
        return arr
          ?.map((item) => {
            return item?.name;
          })
          .join(', ');
      }
      return null;
    };
    return { include: parse(include), exclude: parse(exclude) };
  };

  const detailedTargetingData = onGetDetailTargeting();

  return (
    <Wrapper>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Description label="Ad Set Name" value={adSetData?.name} />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Description
            label="Dynamic Creative"
            value={adSetData?.is_dynamic_creative ? 'On' : 'Off'}
          />
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col gutter={[24, 24]}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Optimization & Delivery
            </Col>
            <Col span={24}>
              <Description
                label="Optimization for Ad Delivery"
                value={adSetData?.optimization_goal}
                onParseValue
              />
            </Col>
            <Col span={24}>
              <Description
                label={
                  adSetData?.bid_amount ? 'Bid Control' : 'Cost per result goal'
                }
                value={
                  adSetData?.bid_amount ? toIDR(adSetData?.bid_amount) : null
                }
              />
            </Col>
            <Col span={24}>
              <Row gutter={16}>
                <Col span={12}>
                  <Description
                    label="When You Get Charged"
                    value={adSetData?.billing_event}
                    onParseValue
                  />
                </Col>
                <Col span={12}>
                  <Description label="Delivery type" value="Standard" />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>

        <Col gutter={[24, 24]}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Budget & Schedule
            </Col>
            <Col span={24}>
              <Description label="Budget" value={onGetCampaignBudget()} />
            </Col>
            <Col span={24}>
              <Row gutter={16}>
                <Col span={12}>
                  <Description label="Start Date" value={startDate} />
                </Col>
                <Col span={12}>
                  <Description label="End Date" value={endDate} />
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  <Description
                    label="Ad Set Spend Limits (Optional)"
                    value={spendLimitData?.required ? 'On' : 'Off'}
                  />
                </Col>
                {spendLimitData?.required && (
                  <Col span={24}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Description
                          label={`${spendLimitData?.label} Minimum`}
                          value={spendLimitData?.min || '-'}
                        />
                      </Col>
                      <Col span={12}>
                        <Description
                          label={`${spendLimitData?.label} Maximum`}
                          value={spendLimitData?.max || '-'}
                        />
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col gutter={[24, 24]}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Row gutter={[12, 12]}>
                <Col span={24} className="font-subtitle2_medium black1">
                  Audience
                </Col>
                <Col span={24} className="font-paragraph black2">
                  Define who you want to see your ads.
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Description label="Location" value={onGetLocation()} />
            </Col>
            <Col span={24}>
              <Row gutter={16}>
                <Col span={12}>
                  <Description
                    label="Age"
                    value={`${adSetData?.targeting?.age_min} - ${adSetData?.targeting?.age_max}`}
                  />
                </Col>
                <Col span={12}>
                  <Description label="Gender" value={onGetGender()} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Detailed Targeting
            </Col>
            <Col span={24}>
              <Description
                label="Include people who match"
                value={detailedTargetingData.include}
              />
            </Col>
            <Col span={24}>
              <Description
                label="Exclude people who match"
                value={detailedTargetingData.exclude}
              />
            </Col>
            <Col span={24}>
              <Description label="Languages" value={onGetLanguages()} />
            </Col>
            <Col span={24}>
              <Description
                label="Connection"
                value={
                  adSetData?.targeting?.audience_connection
                    ?.audience_connection_display
                }
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Placements
            </Col>
            <Col span={24}>
              <Description
                label={adSetData?.placement_type?.label}
                value={adSetData?.placement_type?.description}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-subtitle2_medium black1">
                  Brand Safety
                </Col>
                <Col span={24} className="font-paragraph black2">
                  Prevent your ads from appearing within or alongside content
                  that&apos;s not conducive to your brand.
                </Col>
              </Row>
            </Col>
            <Col span={24} className="font-paragraph_medium black3">
              Inventory Filter
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black2">
                  Facebook In-Stream Videos, Facebook Instant Articles and
                  Instagram TV
                </Col>
                <Col span={24}>
                  <Row wrap={false}>
                    <Col className="font-paragraph black3">
                      Current filter setting:&nbsp;
                    </Col>
                    <Col className="font-paragraph_medium black1">
                      {adSetData?.facebook_inventory?.label}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[4, 4]}>
                <Col span={24} className="font-paragraph_medium black2">
                  Audience Network
                </Col>
                <Col span={24}>
                  <Row wrap={false}>
                    <Col className="font-paragraph black3">
                      Current filter setting:&nbsp;
                    </Col>
                    <Col className="font-paragraph_medium black1">
                      {adSetData?.audience_network?.label}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[12, 12]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Block Lists
            </Col>
            <Col span={24} className="font-paragraph black2">
              {adSetData?.block_lists || "You don't have any block lists."}
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Divider style={{ margin: 0 }} />
        </Col>
        <Col span={24}>
          <Row gutter={[12, 12]}>
            <Col span={24} className="font-subtitle2_medium black1">
              Content Type Exclusions
            </Col>
            <Col span={24} className="font-paragraph black2">
              {adSetData?.exclude_all_live_stream || 'None'}
            </Col>
          </Row>
        </Col>
      </Row>
    </Wrapper>
  );
};

export default AdSet;
